import React, { useContext, useCallback, useState, useMemo } from 'react';
import { AppContext } from '@prisjakt/ui-app-context';
import ThirdLevelCategory from './third-level-category';
import { Category } from './types';
import { trackCategoryClick } from './tracking';
import * as S from './styles';

type Props = {
  category: Category;
  onClickCategoryLink: (id: string) => void;
};

const sortCategoriesAlphabetically = (categories: Category[], locale: string) =>
  categories.slice().sort((a, b) => a.name.localeCompare(b.name, locale));

export const maxSubcategoriesWhenCollapsed = 4;

const SecondLevelCategory = ({ category, onClickCategoryLink }: Props) => {
  const { id, name, children = [] } = category;
  const { t, market } = useContext(AppContext);
  const [isExpanded, setExpanded] = useState(false);

  const toggleExpanded = useCallback(() => {
    setExpanded(!isExpanded);
  }, [isExpanded]);

  const visibleChildren = useMemo(
    () =>
      sortCategoriesAlphabetically(
        isExpanded || children.length <= maxSubcategoriesWhenCollapsed
          ? children
          : children.slice(0, maxSubcategoriesWhenCollapsed),
        market.locale.replace('_', '-')
      ),
    [children, isExpanded, market.locale]
  );

  const onClickCurrentCategoryLink = useCallback(() => {
    trackCategoryClick(category, 1);
    onClickCategoryLink(id);
  }, [id, category, onClickCategoryLink]);

  const onClickThirdLevelCategory = useCallback(
    (childId: string) => {
      const childCategory = children.find(c => c.id === childId);
      if (childCategory) {
        trackCategoryClick(childCategory, 2);
      }
      onClickCategoryLink(childId);
    },
    [children, onClickCategoryLink]
  );

  return (
    <S.SecondLevelItem>
      <S.SecondLevelHeading title={name}>
        <S.SecondLevelLink
          onClick={onClickCurrentCategoryLink}
          category={category}
        >
          {name}
        </S.SecondLevelLink>
      </S.SecondLevelHeading>

      {visibleChildren.length > 0 && (
        <S.ThirdLevelMenu>
          {visibleChildren.map(thirdLevelCategory => (
            <ThirdLevelCategory
              key={thirdLevelCategory.id}
              category={thirdLevelCategory}
              onClickCategoryLink={onClickThirdLevelCategory}
            />
          ))}
        </S.ThirdLevelMenu>
      )}

      {(children.length > visibleChildren.length || isExpanded) && (
        <S.ShowAllButton onClick={toggleExpanded}>
          {isExpanded
            ? t('header', 'mega-menu.show-fewer')
            : t('header', 'mega-menu.show-all-count', {
                count: children.length,
              })}
        </S.ShowAllButton>
      )}
    </S.SecondLevelItem>
  );
};

export default SecondLevelCategory;
